import Marquee from "react-fast-marquee";
import { Reveal } from "./Reveal";
import { NomoexCard3D } from "./NomoexCard3D";
import { Zap, Globe2, Percent, AtSign, ShieldCheck, ArrowUpRight, ArrowRight } from "lucide-react";

const cardPerks = [
  { icon: Globe2, text: "Spend anywhere cards are accepted" },
  { icon: Percent, text: "0.50% top-up from your USDT/USDC balance" },
  { icon: ShieldCheck, text: "Issued via licensed card partners" },
];

const p2pPerks = [
  { icon: AtSign, text: "Send by @username — no wallet addresses" },
  { icon: Zap, text: "Instant & free between Nomoex accounts" },
  { icon: Globe2, text: "Cross-chain settlement handled for you" },
];

export const CardP2P = () => (
  <section id="card" className="noise-overlay relative overflow-hidden bg-[#0A0C14] py-24 lg:py-32">
    <div className="absolute -top-40 left-1/3 h-96 w-96 rounded-full bg-[#3B5FE8]/20 blur-[140px]" aria-hidden="true" />
    <div className="marquee-fade relative z-10 mb-16 select-none" aria-hidden="true">
      <Marquee speed={35} gradient={false}>
        {["Spend anywhere", "Instant P2P", "Zero borders", "One balance"].map((t) => (
          <span
            key={t}
            className="font-heading mx-8 text-4xl font-semibold tracking-tighter text-transparent sm:mx-10 sm:text-5xl lg:text-7xl"
            style={{ WebkitTextStroke: "1px rgba(255,255,255,0.16)" }}
          >
            {t}
          </span>
        ))}
      </Marquee>
    </div>
    <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="overline mb-4">Spend &amp; send</p>
        <h2 className="font-heading max-w-2xl text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
          The future of money, <span className="text-[#7B96FF]">in the real world.</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid gap-8 lg:grid-cols-5">
        <Reveal delay={0.1} className="lg:col-span-3">
          <div data-testid="nomoex-card-section" className="group h-full rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl transition-colors duration-300 hover:border-[#3B5FE8]/50 lg:p-12">
            <NomoexCard3D className="mx-auto w-full max-w-md" />
            <h3 className="font-heading mt-10 text-2xl font-medium text-white lg:text-3xl">The Nomoex Card</h3>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400 lg:text-base">
              A card funded directly by your digital dollar balance. Coffee in Lisbon, servers in Singapore, ads in São Paulo — top up in USDT, spend like it's fiat.
            </p>
            <ul className="mt-6 space-y-3">
              {cardPerks.map((p) => (
                <li key={p.text} className="flex items-center gap-3 text-sm text-slate-300">
                  <p.icon size={16} className="shrink-0 text-[#7B96FF]" /> {p.text}
                </li>
              ))}
            </ul>
            <a
              href="#waitlist"
              data-testid="card-waitlist-cta"
              className="btn-primary mt-9 inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold"
            >
              Get the Card first <ArrowRight size={15} />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="lg:col-span-2">
          <div data-testid="p2p-section" className="group h-full rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl transition-colors duration-300 hover:border-[#3B5FE8]/50 lg:p-12">
            <div className="mx-auto max-w-sm space-y-3">
              {[
                { from: "@lena", to: "@marco", amt: "250 USDT", note: "Design sprint", status: "Instant" },
                { from: "@você", to: "@studio_kr", amt: "1,800 USDC", note: "Invoice #221", status: "Instant" },
                { from: "@arjun", to: "@você", amt: "96 USDT", note: "Dinner split", status: "Instant" },
              ].map((t, i) => (
                <div key={i} className={`flex items-center justify-between rounded-2xl border border-white/10 bg-[#10131f] px-5 py-4 transition-transform duration-500 ${i === 1 ? "group-hover:scale-[1.03] border-[#3B5FE8]/40" : ""}`}>
                  <div>
                    <p className="text-sm font-medium text-white">{t.from} <ArrowUpRight size={12} className="inline text-[#7B96FF]" /> {t.to}</p>
                    <p className="text-xs text-slate-500">{t.note}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-white">{t.amt}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">{t.status}</p>
                  </div>
                </div>
              ))}
            </div>
            <h3 className="font-heading mt-10 text-2xl font-medium text-white">P2P, without the friction</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400 lg:text-base">
              Money between people should feel like a message. Send money to any Nomoex user instantly — no addresses, no gas anxiety, no waiting.
            </p>
            <ul className="mt-6 space-y-3">
              {p2pPerks.map((p) => (
                <li key={p.text} className="flex items-center gap-3 text-sm text-slate-300">
                  <p.icon size={16} className="shrink-0 text-[#7B96FF]" /> {p.text}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
